/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";

const DOMAINS = [
  { key: "housing", label: "Housing", note: "where you sleep, where you live", color: "#2B4522" },
  { key: "whanau", label: "Whānau", note: "your tamariki, your people", color: "#8B3A33" },
  { key: "justice", label: "Justice / probation", note: "PO, conditions, court stuff", color: "#4A5568" },
  { key: "finances", label: "Pūtea · finances", note: "income, bills, survival", color: "#D4A373" },
  { key: "identity", label: "Identity · voice", note: "who you are, your mana", color: "#9C6644" },
  { key: "community", label: "Community", note: "isolation vs connection", color: "#6B705C" },
  { key: "education", label: "Education · mahi", note: "learning, work, kura", color: "#B5838D" },
];

export default function Wheel() {
  const [scores, setScores] = useState({
    housing: 5, whanau: 5, justice: 5, finances: 5, identity: 5, community: 5, education: 5,
  });
  const [reflection, setReflection] = useState("");
  const [loading, setLoading] = useState(false);
  const [latest, setLatest] = useState(null);

  useEffect(() => {
    api.get("/wheel/latest").then((r) => {
      if (r.data) {
        setLatest(r.data);
        const next = {};
        DOMAINS.forEach((d) => (next[d.key] = r.data[d.key] ?? 5));
        setScores(next);
      }
    }).catch(() => {});
  }, []);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/wheel", { ...scores, reflection: reflection || null });
      toast.success("The wheel turns.");
      const fresh = await api.get("/wheel/latest");
      setLatest(fresh.data);
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 animate-fadeup">
      <header>
        <div className="text-[11px] uppercase tracking-[0.22em] text-kokowai mb-2">Te wīra · the wheel</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-charcoal mb-2">The Reintegration Wheel</h1>
        <p className="text-charcoal/70 max-w-2xl mb-4">
          Seven domains around the outside. The belief triangle inside.
          The whare at the centre. Rate each spoke 0–10. This isn't a test —
          it's a way of <em>seeing</em> where the imbalance lives.
        </p>
        <div className="text-sm text-charcoal/80 max-w-2xl bg-muka/70 border border-border rounded-2xl p-5 leading-relaxed space-y-3">
          <div className="font-serif text-base text-kokowai">The three layers</div>
          <p>
            <span className="font-medium">The whare at the centre</span> is yourself — Te Whare Tapa Whā,
            your four walls of wellbeing.
            <span className="font-medium"> The belief triangle</span> around it is how you show up in the world —
            <span className="italic"> benefits</span> on one side,
            <span className="italic"> behaviours</span> on the other,
            <span className="italic"> belief</span> at the apex binding them.
            <span className="font-medium"> The seven spokes</span> of the wheel are the externals you walk through —
            housing, whānau, justice, finances, identity, community, education.
          </p>
        </div>
      </header>

      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2">
          <div className="sticky top-20 bg-muka rounded-3xl p-6 border border-border">
            <WheelChart scores={scores} />
            <div className="text-center text-xs text-muted-foreground mt-3">
              {latest ? "Last saved · weaving forward" : "Set your spokes — turn the wheel"}
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 space-y-3">
          {DOMAINS.map((d) => (
            <DomainRow
              key={d.key}
              domain={d}
              value={scores[d.key]}
              onChange={(v) => setScores((p) => ({ ...p, [d.key]: v }))}
            />
          ))}
          <label className="block pt-2">
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              What stood out (optional)
            </span>
            <textarea
              rows={3}
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              data-testid="wheel-reflection"
              className="mt-2 w-full px-4 py-3 rounded-xl border border-border bg-muka focus:outline-none focus:ring-2 focus:ring-ngahere"
              placeholder="One thing I'm noticing..."
            />
          </label>
          <button
            onClick={submit}
            disabled={loading}
            data-testid="wheel-save"
            className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-ngahere text-muka hover:bg-ngahere/90 disabled:opacity-50"
          >
            <Save size={18} /> {loading ? "..." : "Tukua · save the turn"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DomainRow({ domain, value, onChange }) {
  return (
    <div className="bg-muka rounded-2xl p-4 sm:p-5 border border-border">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <div className="font-serif text-lg text-charcoal" style={{ color: domain.color }}>
            {domain.label}
          </div>
          <div className="text-xs text-muted-foreground">{domain.note}</div>
        </div>
        <div className="font-serif text-3xl tabular-nums" style={{ color: domain.color }}>
          {value}
        </div>
      </div>
      <input
        type="range"
        min="0"
        max="10"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={`wheel-slider-${domain.key}`}
        className="w-full h-2 rounded-full appearance-none cursor-pointer"
        style={{ accentColor: domain.color }}
      />
    </div>
  );
}

function WheelChart({ scores }) {
  const size = 320, cx = size / 2, cy = size / 2, R = 130;
  const keys = DOMAINS;
  const angles = keys.map((_, i) => (i / keys.length) * Math.PI * 2 - Math.PI / 2);
  const pts = keys.map((d, i) => {
    const r = (scores[d.key] / 10) * R;
    return [cx + Math.cos(angles[i]) * r, cy + Math.sin(angles[i]) * r];
  });
  const polygon = pts.map((p) => p.join(",")).join(" ");

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-auto" data-testid="wheel-chart">
      {/* concentric guides */}
      {[0.25, 0.5, 0.75, 1].map((f, i) => (
        <circle key={i} cx={cx} cy={cy} r={R * f} fill="none" stroke="#E5E0D8" strokeWidth="1" />
      ))}
      {/* spokes */}
      {keys.map((d, i) => {
        const x = cx + Math.cos(angles[i]) * R;
        const y = cy + Math.sin(angles[i]) * R;
        return <line key={d.key} x1={cx} y1={cy} x2={x} y2={y} stroke="#E5E0D8" strokeWidth="1" />;
      })}
      {/* shape */}
      <polygon
        points={polygon}
        fill="rgba(43,69,34,0.18)"
        stroke="#2B4522"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      {/* points */}
      {pts.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r="4" fill={keys[i].color} />
      ))}
      {/* labels */}
      {keys.map((d, i) => {
        const x = cx + Math.cos(angles[i]) * (R + 22);
        const y = cy + Math.sin(angles[i]) * (R + 22);
        return (
          <text
            key={d.key}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily="Manrope"
            fontSize="11"
            fill="#2A2A2A"
          >
            {d.label.split(" ")[0]}
          </text>
        );
      })}
    </svg>
  );
}